import { memo } from 'react';

import { NavLink, useLocation } from 'react-router-dom';

import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';

import BrandMark from '@components/ui/BrandMark';
import navigationConfig from '@config/navigation.config';
import themeConfig from '@config/theme.config';
import { DRAWER_WIDTH } from '@constants/layout';
import { useAuth } from '@hooks';

import { MENU_ITEM_ICON_SIZE, MENU_ITEM_ICON_SX, MENU_ITEM_SX } from '../menuItemStyles';

const AppSidebar = memo(function AppSidebar({ onClose, variant = 'permanent' }) {
  const location = useLocation();
  const { logout } = useAuth();

  return (
    <Box
      component="nav"
      aria-label="Main navigation"
      sx={{
        width: variant === 'temporary' ? 'min(88vw, 300px)' : DRAWER_WIDTH,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#FFFFFF',
        borderRight: '1px solid',
        borderColor: themeConfig.borderColor,
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          px: 2,
          height: 64,
          display: 'flex',
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: themeConfig.borderColor,
        }}
      >
        <BrandMark height={40} />
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 1.5,
          py: 2,
          minHeight: 0,
          scrollbarWidth: 'thin',
        }}
      >
        {navigationConfig.sections.map((section) => (
          <Box key={section.id} sx={{ mb: section.label ? 2.5 : 1.5 }}>
            {section.label ? (
              <Typography
                sx={{
                  px: 1.25,
                  mb: 0.75,
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: themeConfig.textMuted,
                }}
              >
                {section.label}
              </Typography>
            ) : null}

            <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {section.items.map((item) => {
                const Icon = item.icon;
                const selected = location.pathname === item.path;

                return (
                  <ListItemButton
                    key={item.id}
                    component={NavLink}
                    to={item.path}
                    selected={selected}
                    onClick={() => onClose?.()}
                    sx={{
                      ...MENU_ITEM_SX,
                      color: selected ? themeConfig.primaryColor : MENU_ITEM_SX.color,
                      bgcolor: selected ? themeConfig.primarySoft : 'transparent',
                      '&:hover': {
                        bgcolor: selected
                          ? themeConfig.primarySoftStrong
                          : themeConfig.surfaceSecondary,
                        color: selected ? themeConfig.primaryColor : themeConfig.textPrimary,
                      },
                      '&.Mui-selected': {
                        bgcolor: themeConfig.primarySoft,
                        color: themeConfig.primaryColor,
                        '&:hover': { bgcolor: themeConfig.primarySoftStrong },
                      },
                      '&.Mui-focusVisible': {
                        outline: `2px solid ${themeConfig.primaryColor}`,
                        outlineOffset: 1,
                      },
                    }}
                  >
                    <ListItemIcon sx={MENU_ITEM_ICON_SX}>
                      <Icon sx={{ fontSize: MENU_ITEM_ICON_SIZE }} />
                    </ListItemIcon>
                    {item.label}
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          px: 1.5,
          py: 1.5,
          borderTop: '1px solid',
          borderColor: themeConfig.borderColor,
        }}
      >
        <ListItemButton
          component="button"
          type="button"
          aria-label="Sign out"
          onClick={() => {
            logout();
            onClose?.();
          }}
          sx={{
            ...MENU_ITEM_SX,
            width: '100%',
            bgcolor: 'transparent',
            border: '1px solid',
            borderColor: themeConfig.borderColor,
            transition: 'background-color 160ms ease, border-color 160ms ease, color 160ms ease',
            '&:hover': {
              bgcolor: themeConfig.roseSoft,
              borderColor: themeConfig.roseBorder,
              color: themeConfig.roseIcon,
            },
            '&:active': {
              bgcolor: themeConfig.roseSoft,
              borderColor: themeConfig.roseBorder,
              color: themeConfig.roseText,
            },
            '&.Mui-focusVisible': {
              outline: `2px solid ${themeConfig.errorColor}`,
              outlineOffset: 1,
            },
          }}
        >
          <ListItemIcon sx={MENU_ITEM_ICON_SX}>
            <LogoutOutlinedIcon sx={{ fontSize: MENU_ITEM_ICON_SIZE }} />
          </ListItemIcon>
          Sign out
        </ListItemButton>
      </Box>
    </Box>
  );
});

AppSidebar.propTypes = {
  onClose: PropTypes.func,
  variant: PropTypes.oneOf(['permanent', 'temporary']),
};

export default AppSidebar;
